import {
	defaultLocale,
	getLocalesForEnvironment,
	type Locale,
	productionLocales,
	testLocale,
} from "./locales";

export const locales: readonly Locale[] = getLocalesForEnvironment();
export type { Locale } from "./locales";
export const LOCALE_COOKIE_NAME = "NEXT_LOCALE";
export const LOCALE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const localeSet = new Set<string>(locales);
const sharedDevCookieEnvironments = new Set([
	"dev",
	"staging",
	"preview",
	"development",
]);

export function isLocale(value: string | null | undefined): value is Locale {
	return typeof value === "string" && localeSet.has(value);
}

export function resolveNearestSupportedLocale<TLocale extends string = Locale>(
	requested: string | null | undefined,
	supportedLocales: readonly TLocale[] = locales as readonly TLocale[],
): TLocale | null {
	const normalizedRequested = normalizeLocaleTag(requested);
	if (!normalizedRequested) {
		return null;
	}

	const normalizedSupported = supportedLocales.map((locale) => ({
		locale,
		normalized: normalizeLocaleTag(locale),
	}));
	const exactMatch = normalizedSupported.find(
		({ normalized }) => normalized === normalizedRequested,
	);
	if (exactMatch) {
		return exactMatch.locale;
	}

	const baseLanguage = normalizedRequested.split("-")[0];
	if (!baseLanguage) {
		return null;
	}

	const baseMatch = normalizedSupported.find(
		({ normalized }) => normalized === baseLanguage,
	);
	if (baseMatch) {
		return baseMatch.locale;
	}

	const languageMatch = normalizedSupported.find(({ normalized }) =>
		normalized?.startsWith(`${baseLanguage}-`),
	);
	return languageMatch?.locale ?? null;
}

function normalizeLocaleTag(value: string | null | undefined): string | null {
	const normalized = value?.trim().replaceAll("_", "-").toLowerCase();
	return normalized ? normalized : null;
}

export function getLocaleCookieOptions(
	environment = getLocaleCookieEnvironment(),
) {
	const sharedDomain =
		environment === "production"
			? ".klarify.ca"
			: sharedDevCookieEnvironments.has(environment ?? "")
				? ".klarify-dev.ca"
				: undefined;

	return {
		name: LOCALE_COOKIE_NAME,
		maxAge: LOCALE_COOKIE_MAX_AGE_SECONDS,
		path: "/",
		sameSite: "lax" as const,
		secure: Boolean(sharedDomain),
		...(sharedDomain ? { domain: sharedDomain } : {}),
	};
}

function getLocaleCookieEnvironment() {
	const deployedEnvironment =
		process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.VERCEL_ENV;
	if (deployedEnvironment) {
		return deployedEnvironment;
	}

	return process.env.NODE_ENV === "production" ? "production" : "local";
}

export function getLocaleCookie(
	locale: Locale,
	environment?: Parameters<typeof getLocaleCookieOptions>[0],
) {
	return {
		...getLocaleCookieOptions(environment),
		value: locale,
	};
}

// NextResponse.rewrite only forwards modified upstream request cookies through
// request header overrides, so build a cloned Headers object instead of
// mutating request.cookies.
export function getHeadersWithLocaleCookie(
	headers: Headers,
	locale: Locale,
): Headers {
	const nextHeaders = new Headers(headers);
	const localeCookie = `${LOCALE_COOKIE_NAME}=${encodeURIComponent(locale)}`;
	const cookieHeader = nextHeaders.get("cookie");

	if (!cookieHeader) {
		nextHeaders.set("cookie", localeCookie);
		return nextHeaders;
	}

	let replacedLocaleCookie = false;
	const cookies = cookieHeader
		.split(";")
		.map((cookie) => {
			const trimmed = cookie.trim();
			if (!trimmed) {
				return null;
			}

			const [name] = trimmed.split("=");
			if (name?.trim() === LOCALE_COOKIE_NAME) {
				replacedLocaleCookie = true;
				return localeCookie;
			}

			return trimmed;
		})
		.filter((cookie): cookie is string => Boolean(cookie));

	if (!replacedLocaleCookie) {
		cookies.push(localeCookie);
	}

	nextHeaders.set("cookie", cookies.join("; "));
	return nextHeaders;
}

export function resolveLocaleFromCookieValue(
	value: string | null | undefined,
): Locale | null {
	return resolveNearestSupportedLocale(value, getResolvableLocales());
}

export function resolveLocaleFromCookieHeader(
	header: string | null | undefined,
): Locale | null {
	if (!header) {
		return null;
	}

	for (const part of header.split(";")) {
		const [name, ...valueParts] = part.trim().split("=");
		if (name?.trim() !== LOCALE_COOKIE_NAME) {
			continue;
		}
		try {
			return resolveLocaleFromCookieValue(
				decodeURIComponent(valueParts.join("=").trim()),
			);
		} catch {
			return null;
		}
	}

	return null;
}

export function resolveLocaleFromAcceptLanguageHeader(
	header: string | null | undefined,
): Locale {
	if (!header) {
		return defaultLocale;
	}

	for (const { tag } of parseAcceptLanguageHeader(header)) {
		const locale = resolveNearestSupportedLocale(tag, getResolvableLocales());
		if (locale) {
			return locale;
		}
	}

	return defaultLocale;
}

function getResolvableLocales(): readonly Locale[] {
	return process.env.NODE_ENV === "test"
		? [...productionLocales, testLocale]
		: locales;
}

export function resolveLocaleFromHeaders(headers: Headers): Locale {
	const cookieLocale = resolveLocaleFromCookieHeader(headers.get("cookie"));
	if (cookieLocale) {
		return cookieLocale;
	}

	return resolveLocaleFromAcceptLanguageHeader(headers.get("accept-language"));
}

function parseAcceptLanguageHeader(header: string) {
	return header
		.split(",")
		.map((part) => {
			const [rawTag, ...params] = part.trim().split(";");
			const tag = rawTag?.toLowerCase();
			if (!tag) {
				return null;
			}
			const quality = params
				.map((param) => param.trim())
				.find((param) => param.startsWith("q="))
				?.slice(2);
			return {
				tag,
				q: quality ? Number.parseFloat(quality) : 1,
			};
		})
		.filter((entry): entry is { tag: string; q: number } => Boolean(entry))
		.sort((a, b) => b.q - a.q);
}

export { defaultLocale, productionLocales };
